import type { ResolveUploadInput } from "@shelflist/shared";
import { hasRole } from "@shelflist/shared";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookPlus, Inbox, Paperclip, Trash2, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useFormat } from "@/i18n/format";
import { api, call } from "@/lib/api";
import { pendingUploadsQuery, queryKeys } from "@/lib/queries";
import { useMe } from "@/lib/session";

export const Route = createFileRoute("/_app/upload/inbox")({
  loader: ({ context }) =>
    hasRole(context.me, "editor")
      ? context.queryClient.ensureQueryData(pendingUploadsQuery)
      : undefined,
  component: InboxPage,
});

type PendingUpload = Awaited<
  ReturnType<NonNullable<typeof pendingUploadsQuery.queryFn>>
>["uploads"][number];

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function InboxPage() {
  const { t } = useTranslation("upload");
  const { data } = useSuspenseQuery(pendingUploadsQuery);
  return (
    <Page title={t("inbox.title")} description={t("inbox.description")}>
      {data.uploads.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={t("inbox.empty")}
          action={
            <Button asChild variant="outline">
              <Link to="/upload">
                <Upload aria-hidden />
                {t("inbox.uploadMore")}
              </Link>
            </Button>
          }
        />
      ) : (
        <ul className="grid gap-4">
          {data.uploads.map((pending) => (
            <li key={pending.id}>
              <PendingUploadCard pending={pending} />
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}

export function PendingUploadCard({ pending }: { pending: PendingUpload }) {
  const { t } = useTranslation("upload");
  const format = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const { metadata } = pending;

  const resolve = useMutation({
    mutationFn: (input: ResolveUploadInput) =>
      call(api.uploads[":id"].resolve.$post({ param: { id: pending.id }, json: input })),
    onSuccess: (result, input) => {
      if (result.status === "completed") {
        const bookId = result.bookId;
        const message =
          input.action === "attach"
            ? t("inbox.attached", {
                title: pending.candidates.find((c) => c.id === bookId)?.title ?? metadata.title,
              })
            : t("inbox.created");
        toast.success(message, {
          action: {
            label: t("actions.openBook"),
            onClick: () => window.location.assign(`/books/${bookId}`),
          },
        });
      } else {
        toast.success(t("inbox.discarded"));
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.uploads }),
  });

  const createButton = (
    <Button
      variant="outline"
      disabled={resolve.isPending}
      onClick={
        pending.sameTitleBooks.length > 0 ? undefined : () => resolve.mutate({ action: "create" })
      }
    >
      <BookPlus aria-hidden />
      {t("inbox.createNew")}
    </Button>
  );

  const authors = metadata.authors.map((a) => a.name).join(", ");
  return (
    <Card data-testid="pending-upload">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
          {metadata.title}
          <Badge variant="secondary">{pending.format.toUpperCase()}</Badge>
        </CardTitle>
        <CardDescription className="grid gap-0.5">
          <span>{authors || t("inbox.unknownAuthor")}</span>
          {metadata.series ? (
            <span>
              {metadata.series.index !== undefined
                ? t("inbox.seriesWithIndex", {
                    name: metadata.series.name,
                    index: metadata.series.index,
                  })
                : t("inbox.series", { name: metadata.series.name })}
            </span>
          ) : null}
          <span>
            {pending.originalFilename} · {formatSize(pending.sizeBytes)} ·{" "}
            {format.dateTime(pending.createdAt)}
            {hasRole(me, "admin")
              ? ` · ${t("inbox.uploader", { name: pending.uploader.name })}`
              : ""}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-2">
          <h3 className="text-sm font-medium">{t("inbox.candidates")}</h3>
          {pending.candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("inbox.noCandidates")}</p>
          ) : (
            <ul className="grid gap-2">
              {pending.candidates.map((candidate) => (
                <li
                  key={candidate.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <div>
                    <div className="font-medium">{candidate.title}</div>
                    <div className="text-muted-foreground text-sm">
                      {candidate.authors.join(", ")}
                      {" · "}
                      {candidate.formats.length > 0 ? (
                        candidate.formats.map((f) => f.toUpperCase()).join(", ")
                      ) : (
                        <Badge variant="outline">{t("inbox.noFormats")}</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ action: "attach", bookId: candidate.id })}
                  >
                    <Paperclip aria-hidden />
                    {t("inbox.attach")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {pending.sameTitleBooks.length > 0 ? (
            <ConfirmDialog
              title={t("inbox.sameTitleTitle", { title: metadata.title })}
              description={
                <span className="grid gap-2">
                  <span>{t("inbox.sameTitleDescription")}</span>
                  {pending.sameTitleBooks.map((existing) => (
                    <span key={existing.id} className="block">
                      <Link
                        className="text-primary underline underline-offset-4"
                        to="/books/$id"
                        params={{ id: existing.id }}
                      >
                        {existing.title}
                      </Link>
                      {" — "}
                      {existing.authors.length > 0
                        ? existing.authors.join(", ")
                        : t("inbox.unknownAuthor")}
                    </span>
                  ))}
                </span>
              }
              confirmLabel={t("inbox.createAnyway")}
              onConfirm={() => resolve.mutate({ action: "create" })}
              trigger={createButton}
            />
          ) : (
            createButton
          )}
          <ConfirmDialog
            destructive
            title={t("inbox.discardTitle", { name: pending.originalFilename })}
            description={t("inbox.discardDescription")}
            confirmLabel={t("inbox.discard")}
            onConfirm={() => resolve.mutate({ action: "discard" })}
            trigger={
              <Button variant="ghost" disabled={resolve.isPending}>
                <Trash2 aria-hidden />
                {t("inbox.discard")}
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
